// Adapter from /admin/fatigue/workforce to the scoring engine's roster.
//
// The API supplies only what the wearable data supports. Every input the source
// cannot give (intraday HR, rota, assessment, EDA) is set to a neutral value that
// makes the dependent formula inert, and `coverage` tells the screens not to
// present those scores at all.

import { rotaWeek } from '../../data/rota';
import type { RotaShift } from '../../data/rota';
import { calcFED } from './scores';
import { REGIONS, replaceRoster, wArr } from './data';
import type { Group, RawWorker, Region, Worker } from './data';
import type { PastCfg, PastRange } from '../screens/past/config';

export interface WireWorker {
  id: string;
  name: string;
  role: string;
  group: number;
  asOf: string;
  hrv: number;
  rhr: number;
  slp: number;
  hrv_b: number;
  rhr_b: number;
  slp_b: number;
  slp7: number[];
  slp_efficiency: number | null;
  slp_onset_drift: number | null;
  baselineDays: number;
  coverage: NonNullable<RawWorker['coverage']>;
  history: { day: string; hrv: number | null; rhr: number | null; slp: number | null }[];
}

export interface WireWorkforce {
  asOf: string | null;
  enrolled: number;
  regions: { id: string; name: string; groups: number[] }[];
  groups: Group[];
  workers: WireWorker[];
  rota?: RotaShift[];
}

type Day = WireWorker['history'][number];

/** Daily history per live worker (keyed by Worker.init) plus the latest reading date across the roster. */
const LIVE = { history: {} as Record<string, Day[]>, asOf: '' };

const REGION_COLOURS = ['#2f6fd0', '#d9622f', '#1a9a6c', '#7c70d6', '#d46a94'];

const mean = (v: number[]) => (v.length ? v.reduce((a, b) => a + b, 0) / v.length : null);

const initialsOf = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join('') || 'W';

const DAY_MS = 86_400_000;
const iso = (ms: number) => new Date(ms).toISOString().slice(0, 10);

/** FED on a given day, scored against the trailing 28-day baseline before it. Null when the day or baseline is too thin. */
function fedOn(byDay: Map<string, Day>, ms: number): number | null {
  const at = (offset: number) => byDay.get(iso(ms - offset * DAY_MS));
  const today = at(0);
  if (!today || today.hrv === null || today.rhr === null || today.slp === null) return null;
  const prior = Array.from({ length: 28 }, (_, i) => at(i + 1)).filter((d): d is Day => !!d);
  const hrvB = mean(prior.map((d) => d.hrv).filter((v): v is number => v !== null));
  const rhrB = mean(prior.map((d) => d.rhr).filter((v): v is number => v !== null));
  const slpB = mean(prior.map((d) => d.slp).filter((v): v is number => v !== null));
  if (hrvB === null || rhrB === null || slpB === null || prior.length < 7) return null;
  const slp7 = Array.from({ length: 7 }, (_, i) => at(i)?.slp)
    .filter((v): v is number => typeof v === 'number')
    .reverse();
  return calcFED({
    hrv: today.hrv,
    rhr: today.rhr,
    slp: today.slp,
    hrv_b: hrvB,
    rhr_b: rhrB,
    slp_b: slpB,
    slp7,
  } as Worker);
}

/** Weekly FED for the last 12 weeks. */
function weeklyFed(w: WireWorker): number[] {
  const byDay = new Map(w.history.map((h) => [h.day, h]));
  const anchor = Date.parse(w.asOf);
  const out: number[] = [];
  for (let wk = 11; wk >= 0; wk--) {
    const v = fedOn(byDay, anchor - wk * 7 * DAY_MS);
    if (v !== null) out.push(v);
  }
  return out;
}

const avg = (v: (number | null | undefined)[]): number | undefined => {
  const xs = v.filter((x): x is number => typeof x === 'number');
  return xs.length ? Math.round((xs.reduce((a, b) => a + b, 0) / xs.length) * 10) / 10 : undefined;
};

const PERIODS: Record<PastRange, { buckets: number; days: number; label: string; sub: string }> = {
  '7d': { buckets: 7, days: 1, label: 'day', sub: 'Last 7 days' },
  '30d': { buckets: 4, days: 7, label: 'week', sub: 'Last 4 weeks' },
  '90d': { buckets: 12, days: 7, label: 'week', sub: 'Last 12 weeks' },
};

/** The Past screen's period config, derived from the live history (no illustrative figures). */
export function buildLivePastCfg(range: PastRange): PastCfg {
  const p = PERIODS[range];
  const end = Date.parse(LIVE.asOf);
  // Bucket k covers (end - (buckets-k)*days, end - (buckets-k-1)*days]; the last bucket ends on the latest reading.
  const bucketDays = (k: number) =>
    Array.from({ length: p.days }, (_, d) => end - ((p.buckets - 1 - k) * p.days + d) * DAY_MS);

  const workers = wArr();
  const seriesFor = (pick: (byDay: Map<string, Day>, ms: number) => number | null | undefined) => {
    const out: Record<string, (number | undefined)[]> = {};
    for (const w of workers) {
      const byDay = new Map((LIVE.history[w.init] ?? []).map((h) => [h.day, h]));
      out[w.init] = Array.from({ length: p.buckets }, (_, k) => avg(bucketDays(k).map((ms) => pick(byDay, ms))));
    }
    return out;
  };
  const fed = seriesFor(fedOn);
  const slp = seriesFor((m, ms) => m.get(iso(ms))?.slp);
  const hrvDev = (() => {
    const out: Record<string, (number | undefined)[]> = {};
    for (const w of workers) {
      const byDay = new Map((LIVE.history[w.init] ?? []).map((h) => [h.day, h]));
      out[w.init] = Array.from({ length: p.buckets }, (_, k) =>
        avg(
          bucketDays(k).map((ms) => {
            const h = byDay.get(iso(ms))?.hrv;
            return typeof h === 'number' && w.hrv_b > 0 ? ((h - w.hrv_b) / w.hrv_b) * 100 : null;
          }),
        ),
      );
    }
    return out;
  })();

  const across = (series: Record<string, (number | undefined)[]>, subset: typeof workers) =>
    Array.from({ length: p.buckets }, (_, k) => avg(subset.map((w) => series[w.init]?.[k])));

  const regionTrends: Record<string, (number | undefined)[]> = {};
  for (const rid of Object.keys(REGIONS)) {
    regionTrends[rid] = across(
      fed,
      workers.filter((w) => REGIONS[rid].groups.includes(w.group)),
    );
  }
  const company = across(fed, workers);
  const known = company.filter((v): v is number => v !== undefined);
  const labels = Array.from({ length: p.buckets }, (_, k) => {
    const d = new Date(end - (p.buckets - 1 - k) * p.days * DAY_MS);
    return p.days === 1
      ? d.toLocaleDateString('en-GB', { weekday: 'short', timeZone: 'UTC' })
      : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' });
  });
  const heatIdx = range === '7d' ? [0, 1, 2, 3, 4, 5, 6] : range === '30d' ? [0, 1, 2, 3] : [0, 4, 8, 11];

  return {
    xLabels: labels,
    heatCols: heatIdx.map((i) => labels[i]),
    heatIdx,
    subLabel: p.sub,
    regionTrends,
    inOutData: {},
    kpi: { fedDelta: known.length > 1 ? Math.round(known[known.length - 1] - known[0]) : 0 },
    slpTrend: across(slp, workers),
    hrvTrend: across(hrvDev, workers),
    workerSeries: fed,
  };
}

export function loadLiveRoster(data: WireWorkforce): void {
  const used = new Set<string>();
  LIVE.history = {};
  LIVE.asOf = data.asOf ?? '';
  const regions: Record<string, Region> = {};
  data.regions.forEach((r, i) => {
    regions[r.id] = { name: r.name, color: REGION_COLOURS[i % REGION_COLOURS.length], groups: r.groups };
  });

  const workers: RawWorker[] = data.workers.map((w) => {
    let init = initialsOf(w.name);
    for (let n = 2; used.has(init); n++) init = `${initialsOf(w.name)}${n}`;
    used.add(init);
    LIVE.history[init] = w.history;
    const fed12 = weeklyFed(w);
    const week = rotaWeek(data.rota?.filter((s) => s.userId === w.id) ?? []);
    return {
      init,
      userId: w.id,
      rota: data.rota?.filter((s) => s.userId === w.id) ?? [],
      name: w.name,
      group: w.group,
      role: w.role,
      roleType: 'mixed',
      // Static onboarding fields only feed PLI/PSI, which are not scored on live data.
      age: 40,
      weight_kg: 75,
      gender: 'M',
      hrv: w.hrv,
      rhr: w.rhr,
      slp: w.slp,
      hrv_b: w.hrv_b,
      rhr_b: w.rhr_b,
      slp_b: w.slp_b,
      slp7: w.slp7.length ? w.slp7 : [w.slp],
      slp_efficiency: w.slp_efficiency ?? 88,
      slp_onset_drift: w.slp_onset_drift ?? 0,
      // Not sourceable from daily wearable data — neutral, and flagged via `coverage`.
      restHrs: week.shortestRest ?? 0,
      consec: week.longestRun,
      edaScore: 0,
      assessScore: 0,
      hr_work: 0,
      hr_work_b: 0,
      hr_rec: 0,
      hr_rec_b: 0,
      hrv_pre: w.hrv,
      hrv_post: w.hrv,
      shiftHrs: week.hours,
      normalHrs: week.contracted,
      otHrs: week.overtime,
      driveKm: week.drivingKm ?? 0,
      driveDays: week.shifts.filter((s) => (s.drivingKm ?? 0) > 0).length,
      wtrFlag: week.shortRestCount > 0,
      inWorkFed: 0,
      outWorkFed: 0,
      hrv_peak_offset: 0,
      rhr_curve_rmse: 0,
      fed12: fed12.length ? fed12 : [],
      shifts: [],
      coverage: w.coverage,
    };
  });

  replaceRoster(workers, data.groups, regions);

  // Anchor the last trend point to the scored FED, as the demo roster does.
  for (const w of workers as Worker[]) {
    if (!w.fed12.length) w.fed12 = [w.fed];
    else w.fed12[w.fed12.length - 1] = w.fed;
  }
}
